import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

interface Stat {
  icon: string;
  targetValue: number;
  suffix: string;
  label: string;
  color: string;
  prefix?: string;
}

const SuccessMetricsWall: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState<{ [key: number]: number }>({});
  const sectionRef = useRef<HTMLDivElement>(null);

  const stats: Stat[] = [
    {
      icon: 'fas fa-brain',
      targetValue: 100000,
      suffix: '+',
      label: 'Questions in Bank',
      color: '#667eea'
    },
    {
      icon: 'fas fa-book',
      targetValue: 200,
      suffix: '+',
      label: 'Practice Tests',
      color: '#764ba2'
    },
    {
      icon: 'fas fa-robot',
      targetValue: 24,
      suffix: '/7',
      label: 'AI Mentor Availability',
      color: '#2d9cdb'
    },
    {
      icon: 'fas fa-chart-line',
      targetValue: 100,
      suffix: '+',
      label: 'Performance Metrics',
      color: '#27ae60'
    },
    {
      icon: 'fas fa-graduation-cap',
      targetValue: 800,
      suffix: '+',
      label: 'Topics Covered',
      color: '#f39c12'
    },
    {
      icon: 'fas fa-clock',
      targetValue: 60,
      suffix: ' sec',
      label: 'Avg Response Time',
      color: '#e74c3c'
    },
    {
      icon: 'fas fa-mobile-alt',
      targetValue: 100,
      suffix: '%',
      label: 'Mobile Optimized',
      color: '#9b59b6'
    },
    {
      icon: 'fas fa-shield-alt',
      targetValue: 99,
      suffix: '%',
      label: 'Platform Uptime',
      color: '#2d9cdb'
    }
  ];

  useEffect(() => {
    const animateCounters = () => {
      stats.forEach((stat, index) => {
        const duration = 2000;
        const steps = 60;
        const increment = stat.targetValue / steps;
        let currentStep = 0;

        const timer = setInterval(() => {
          currentStep++;
          const currentValue = Math.min(increment * currentStep, stat.targetValue);
          
          setCounts(prev => ({
            ...prev,
            [index]: currentValue
          }));

          if (currentStep >= steps) {
            clearInterval(timer);
          }
        }, duration / steps);
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
            animateCounters();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const formatValue = (value: number, index: number): string => {
    if (index === 4) { // Rating
      return value.toFixed(1);
    }
    return Math.floor(value).toString();
  };

  return (
    <div ref={sectionRef} className="success-metrics-wall py-5" 
      style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #667eea 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
      
      {/* Background Pattern */}
      <div className="position-absolute w-100 h-100 top-0 start-0" style={{ opacity: 0.05 }}>
        <div className="position-absolute rounded-circle" 
          style={{ width: '500px', height: '500px', background: 'white', top: '-10%', right: '-5%' }}></div>
        <div className="position-absolute rounded-circle" 
          style={{ width: '400px', height: '400px', background: 'white', bottom: '-10%', left: '-5%' }}></div>
      </div>

      <div className="container position-relative py-5">
        
        {/* Section Header */}
        <div className="text-center text-white mb-5 pb-4">
          <div className="d-inline-flex align-items-center px-4 py-2 mb-3 rounded-pill"
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)'
            }}>
            <i className="fas fa-chart-bar me-2"></i>
            <span className="fw-semibold">Real Impact, Real Results</span>
          </div>
          <h2 className="display-4 fw-bold mb-3">
            Platform <span style={{ 
              background: 'linear-gradient(to right, #fff, #ffd700)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>Highlights</span>
          </h2>
          <p className="lead mb-0" style={{ opacity: 0.95, maxWidth: '700px', margin: '0 auto' }}>
            Powerful features and comprehensive resources designed to help you 
            ace JEE & NEET with AI-powered personalized learning.
          </p>
        </div>

        {/* Metrics Grid */}
        <div className="row g-4">
          {stats.map((stat, index) => (
            <div key={index} className="col-lg-3 col-md-4 col-sm-6">
              <div className="card h-100 border-0 shadow-lg"
                style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px) scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.15)';
                }}>
                <div className="card-body text-center p-4">
                  
                  {/* Icon */}
                  <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                    style={{
                      width: '80px',
                      height: '80px',
                      background: `linear-gradient(135deg, ${stat.color}, ${stat.color}dd)`,
                      boxShadow: `0 8px 16px ${stat.color}40`
                    }}>
                    <i className={`${stat.icon} fa-2x text-white`}></i>
                  </div>

                  {/* Animated Number */}
                  <div className="mb-2">
                    <span className="display-4 fw-bold" style={{ color: stat.color }}>
                      {stat.prefix || ''}{formatValue(counts[index] || 0, index)}{stat.suffix}
                    </span>
                  </div>

                  {/* Label */}
                  <p className="text-muted fw-semibold mb-0">
                    {stat.label}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Platform Highlights */}
        <div className="row g-4 mt-5 pt-4">
          <div className="col-lg-4">
            <div className="rounded-4 p-4 text-white h-100"
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
              <div className="d-flex align-items-center mb-3">
                <i className="fas fa-robot fa-2x me-3" style={{ color: '#ffd700' }}></i>
                <h5 className="mb-0 fw-bold">AI-Powered Learning</h5>
              </div>
              <p className="mb-0" style={{ opacity: 0.9 }}>
                Get personalized study plans, instant doubt solving, and 
                performance insights powered by advanced AI technology.
              </p>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="rounded-4 p-4 text-white h-100"
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
              <div className="d-flex align-items-center mb-3">
                <i className="fas fa-chart-line fa-2x me-3" style={{ color: '#ffd700' }}></i>
                <h5 className="mb-0 fw-bold">Detailed Analytics</h5>
              </div>
              <p className="mb-0" style={{ opacity: 0.9 }}>
                Track your progress with comprehensive performance metrics, 
                subject-wise analysis, and time management insights.
              </p>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="rounded-4 p-4 text-white h-100"
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
              <div className="d-flex align-items-center mb-3">
                <i className="fas fa-mobile-alt fa-2x me-3" style={{ color: '#ffd700' }}></i>
                <h5 className="mb-0 fw-bold">Learn Anywhere</h5>
              </div>
              <p className="mb-0" style={{ opacity: 0.9 }}>
                Access your preparation material anytime, anywhere with our 
                fully responsive web and mobile applications.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-5 pt-4">
          <h3 className="text-white fw-bold mb-4">
            Start Your Journey Today!
          </h3>
          <Link to="/signup" 
            className="btn btn-light btn-lg px-5 py-3 rounded-pill shadow-lg">
            <i className="fas fa-user-plus me-2"></i>
            Start Your Free Trial
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SuccessMetricsWall;
